<script setup lang="ts">
  import { ref, watchEffect, computed, onMounted } from "vue";
  import { IconArrowLeft, IconSearch, Input, useNotification } from "@ui-kit";
  import { useI18n } from "vue-i18n";
  import { useBarcode, useReadRfid } from "@plugins";
  import { TopBar, StatusBar, ProductNotFound } from "@src/components";
  import { type Product, useItemList, useGetWTFIR, WTFIRInputType, WTFIRTypeData, useLazyProductsLoad } from "@src/api";
  import { useDebounce, useShopZoneHook } from "@src/hooks";
  import { default as SearchProductItem } from "./SearchProductItem.vue";
  import { default as SearchProductCard } from "./SearchProductCard.vue";
  import { default as SearchProductParentItem } from "./SearchProductParentItem.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  useShopZoneHook();
  const { execute: getItemList, isLoading: isLoadingItemList, data: itemList, isError } = useItemList();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: loadItem } = useLazyProductsLoad();

  const selectedItem = ref<Product | null>(null);
  const search = ref<string>("");
  const refSearchInput = ref<{ field: HTMLInputElement } | null>(null);
  const debouncedSearch = useDebounce(search, 500);
  const barcode = useBarcode();
  const readRfid = useReadRfid();
  const notification = useNotification();
  const { t } = useI18n();

  watchEffect(() => {
    if (debouncedSearch.value.length < 4) return;

    getItemList({ article: debouncedSearch.value });
  });

  const handleScanData = async (data: string, input_type: WTFIRInputType) => {
    const resultWTFIR = await getWTFIR({ data, input_type });

    if (
      resultWTFIR.isError.value ||
      ![WTFIRTypeData.barcode, WTFIRTypeData.cis, WTFIRTypeData.epc].includes(resultWTFIR.data.value.type_data) ||
      resultWTFIR.data.value.items.length === 0
    ) {
      notification.show({ title: t("Товар не найден"), type: "error" });
      return;
    }

    const resultLoadItem = await loadItem({ item_id: resultWTFIR.data.value.items });

    if (resultLoadItem.isError.value) return;

    selectedItem.value = resultLoadItem.data.value.items[0];

    handleUnsubscribe();
  };

  const handleSubscribe = () => {
    barcode.subscribe((data) => handleScanData(data, WTFIRInputType.barcode));
    readRfid.subscribe((data) => handleScanData(data.value, WTFIRInputType.rfid));
  };

  const handleClickItem = (item: Product) => {
    selectedItem.value = item;
    handleUnsubscribe();
  };

  const handleUnsubscribe = () => {
    barcode.unsubscribe();
    readRfid.unsubscribe();
  };

  const handleClickBackInCard = () => {
    selectedItem.value = null;
    handleSubscribe();
  };

  const handleScroll = () => {
    if (refSearchInput.value?.field && document.activeElement === refSearchInput.value?.field) {
      refSearchInput.value?.field.blur();
    }
  };

  const visibleCard = computed(() => Boolean(selectedItem.value));
  const visibleNotFound = computed(
    () => search.value.length > 0 && (isError.value || itemList.value?.items.length === 0),
  );
  const visibleItems = computed(
    () => search.value.length > 3 && !visibleCard.value && itemList.value && itemList.value?.items.length > 0,
  );
  const visibleParentItems = computed(() => itemList.value?.items.filter((item) => !item.parent_guid).length > 1);

  onMounted(() => {
    handleSubscribe();
    refSearchInput.value?.field.focus();
  });
</script>

<template>
  <div class="widget__search-product" data-testid="widget-search-product">
    <SearchProductCard
      v-if="visibleCard"
      :itemId="selectedItem?.item_id"
      @back="handleClickBackInCard"
      @close="emit('close')"
    />
    <template v-else>
      <StatusBar />
      <TopBar :title="$t('Поиск товара')" @click:left="emit('close')">
        <template #left>
          <IconArrowLeft />
        </template>
      </TopBar>
      <div class="search-product__content" @scroll="handleScroll">
        <Input
          ref="refSearchInput"
          variant="secondary"
          placeholder="0000-000-000"
          inputMode="numeric"
          :iconAfter="IconSearch"
          v-model="search"
          class="search-product__input"
          v-maska="'####-###-###'"
          data-testid="search-product-input"
        />
        <ProductNotFound v-if="visibleNotFound" />
        <div
          v-else-if="visibleItems"
          class="search-product__items"
          :class="{ 'search-product__items--loading': isLoadingItemList }"
          data-testid="search-product-items"
        >
          <template v-for="item in itemList?.items" :key="item.item_id">
            <SearchProductItem
              v-if="Boolean(item.parent_guid)"
              :image="item.image_url"
              :name="item.title"
              :article="item.article"
              :color="item.color"
              @click="() => handleClickItem(item)"
              data-testid="search-product-item"
            />
            <SearchProductParentItem
              v-else-if="visibleParentItems"
              :article="item.article"
              :name="item.title"
              @click="search = item.article"
              data-testid="search-product-parent-item"
            />
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
  .widget__search-product {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-primary);
    z-index: 1;
  }

  .search-product__input {
    position: sticky;
    top: 0;
    padding-bottom: 12px;
    background-color: var(--sf-base-background-primary);
    z-index: 1;
  }

  .search-product__content {
    display: flex;
    flex-direction: column;
    padding: 0 16px 16px;
    overflow: hidden;
    overflow-y: auto;
  }

  .search-product__items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .search-product__items--loading {
    opacity: 0.4;
  }
</style>
